import React from 'react';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper, Select, TextField, Typography, CircularProgress } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import api from '../../services/api/axiosConfig';
import { DURATIONS, DURATIONS_PRICES } from '../../constants/booking';



const AllBookingsContent = () => {

  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [rebookDialogOpen, setRebookDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [rebookFormData, setRebookFormData] = useState({
    dateTime: '',
    duration: ''
  });
  const [rebookErrors, setRebookErrors] = useState({
    dateTime: '',
    duration: ''
  });

  // Function to calculate booking status
  const getBookingStatus = (check_in: string, check_out: string, bookingStatus?: string) => {
    // If booking is cancelled, return "cancelled"
    if (bookingStatus === "cancelled") {
      return "cancelled";
    }

    const now = new Date();
    const start = new Date(check_in);
    const end = new Date(check_out);

    if (now < start) return "Upcoming";
    if (now > end) return "Completed";
    return "Ongoing"; // check_in <= now <= check_out
  };

  // Format date to YYYY-MM-DD
  const formatDateKey = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date to "Month Day, Year" (e.g., "June 10, 2025")
  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Find all active stays
  const activeStays = React.useMemo(() => {
    return bookings.filter((booking) => {
      if (booking.status === "cancelled") return false;
      const status = getBookingStatus(booking.check_in, booking.check_out, booking.status);
      return status === "Ongoing";
    });
  }, [bookings]);
  
  // Find the most recent past/completed booking (not cancelled)
  const previousStay = React.useMemo(() => {
    const completedBookings = bookings
      .filter((booking) => {
        if (booking.status === "cancelled") return false;
        const status = getBookingStatus(booking.check_in, booking.check_out, booking.status);
        return status === "Completed";
      })
      .sort((a, b) => {
        // Sort by check_out date descending (most recent first)
        const dateA = new Date(a.check_out).getTime();
        const dateB = new Date(b.check_out).getTime();
        return dateB - dateA;
      });
    
    return completedBookings.length > 0 ? completedBookings[0] : null;
  }, [bookings]);

  // Truncate botel name if too long
  const truncateName = (name: string, maxLength: number = 15) => {
    if (!name) return '';
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  // Map bookings to calendar values (multiple stays per date) - show botel names
  const dummyValues: Record<string, string[]> = React.useMemo(() => {
    const values: Record<string, string[]> = {};
    
    bookings.forEach((booking) => {
      if (booking.status === "cancelled") {
        // Skip cancelled bookings
        return;
      }

      const dateKey = formatDateKey(booking.check_in);
      const botelName = booking.botel?.name || 'Unknown Botel';
      
      // Initialize array if it doesn't exist
      if (!values[dateKey]) {
        values[dateKey] = [];
      }
      
      // Add botel name to array
      values[dateKey].push(truncateName(botelName));
    });

    return values;
  }, [bookings]);

  // Fetch all bookings on page load
  useEffect(() => {
    const fetchAllBookings = async () => {
      try {
        setLoading(true);
        const response = await api.get('/bookings/all');
        // Handle both array response and object with data property
        const bookingsData = Array.isArray(response.data) 
          ? response.data 
          : response.data.data || [];
        setBookings(bookingsData);
      } catch (error) {
        console.error("Error fetching bookings:", error);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllBookings();
  }, []);
  // 👉 Put this function at the top of your component
  const getBgColor = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    const d = new Date(dateString);
    d.setHours(0, 0, 0, 0);
  
    // Today
    if (d.getTime() === today.getTime()) {
      return "rgba(5,150,105,0.4)"; 
    }
  
    // Past
    if (d.getTime() < today.getTime()) {
      return "rgba(209,213,219,0.4)"; 
    }
  
    // Future
    return "rgba(91,141,255,0.4)";
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];
  
  const styles: any = {
    container: {
      width: "100%",
      background: "#1F262C",
      padding: "20px",
      borderRadius: "12px",
      color: "white",
      fontFamily: "sans-serif",
      marginBottom:"30px"
    },
    headerBar: {
      display: "flex",
      // justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 0",
      marginBottom: "10px"
    },
    navBtn: {
      padding: "6px 12px",
      background: "#2C353F",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      color: "white",
      marginRight: "10px",
      marginLeft: "10px",
    },
    calendarGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      // gap: "6px",
      width: "100%"
    },
    headerCell: {
      textAlign: "center",
      fontWeight: "bold",
      padding: "10px 0",
      background: "#273645"
    },
    dayCell: {
      minHeight: "90px",
      maxHeight: "90px",
      // background: "#1F262C",
      // borderRadius: "8px",
      // border: "1px solid #3D546B",
      padding: "8px",
      cursor: "default",
      fontSize: "14px",
      overflowY: "auto",
      overflowX: "hidden",
      position: "relative",
      // Custom scrollbar styling
      scrollbarWidth: "thin", // Firefox
      scrollbarColor: "transparent transparent", // Firefox: hidden by default
    },
    dayClickable: {
      // background: "#3776FF",
      color: "white"
    },
    valueLabel: {
      marginTop: "6px",
      fontSize: "11px",
      fontWeight: "bold",
      display: "block",
      lineHeight: "1.3",
      wordBreak: "break-word",
      width: "100%"
    }
  };
  const goToNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToPrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const goToNextYear = () => setCurrentDate(new Date(year + 1, month, 1));
  const goToPrevYear = () => setCurrentDate(new Date(year - 1, month, 1));

  const handleDateClick = (dateKey: string) => {
    const dateValues = dummyValues[dateKey];
    if (dateValues && dateValues.length > 0) {
      alert(`Stays: ${dateValues.join(", ")}`);
    }
  };

  const cells = [];

  // Empty starting cells
  for (let i = 0; i < firstDayIndex; i++) {
    cells.push(<div key={`empty-${i}`}></div>);
  }

  // Days with values
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dateValues = dummyValues[dateKey];
    const hasValue = dateValues && dateValues.length > 0;
  
    // If value exists → bg with opacity
    const bgColor = hasValue ? getBgColor(dateKey) : "#1F262C";
  
    // If value exists → border-left same color as bg
    const dynamicBorderLeft = hasValue ? `4px solid ${bgColor}` : "none";
  
    cells.push(
      <div
        key={day}
        className={hasValue ? "calendar-day-cell" : ""}
        style={{
          ...styles.dayCell,
          background: bgColor,
          ...(hasValue ? styles.dayClickable : {}),
          borderLeft: dynamicBorderLeft
        }}
        onMouseEnter={(e) => {
          if (hasValue) {
            e.currentTarget.style.setProperty('scrollbar-color', '#3D546B #2C353F');
          }
        }}
        onMouseLeave={(e) => {
          if (hasValue) {
            e.currentTarget.style.setProperty('scrollbar-color', 'transparent transparent');
          }
        }}
      >
        {day}
  
        {hasValue && (
          <span style={styles.valueLabel}>
            {dateValues.map((name, idx) => (
              <React.Fragment key={idx}>
                {name}
                {idx < dateValues.length - 1 && <br />}
              </React.Fragment>
            ))}
          </span>
        )}
      </div>
    );
  }
  

  const navigate = useNavigate();

  const handleRebookStay = (booking: any) => {
    if (!booking || !booking.botel) {
      return;
    }

    // Set selected booking and open dialog
    setSelectedBooking(booking);
    
    // Reset form data to empty
    setRebookFormData({
      dateTime: '',
      duration: ''
    });

    setRebookDialogOpen(true);
  };

  const handleRebookInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setRebookFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (rebookErrors[field as keyof typeof rebookErrors]) {
      setRebookErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRebookSelectChange = (event: SelectChangeEvent) => {
    setRebookFormData(prev => ({ ...prev, duration: event.target.value }));
    if (rebookErrors.duration) {
      setRebookErrors(prev => ({ ...prev, duration: '' }));
    }
  };

  const validateRebookForm = () => {
    const newErrors = {
      dateTime: '',
      duration: ''
    };
    let isValid = true;

    if (!rebookFormData.dateTime) {
      newErrors.dateTime = 'Date and time is required';
      isValid = false;
    }

    if (!rebookFormData.duration) {
      newErrors.duration = 'Duration is required';
      isValid = false;
    }

    setRebookErrors(newErrors);
    return isValid;
  };

  const handleRebookSubmit = () => {
    if (!validateRebookForm() || !selectedBooking || !selectedBooking.botel) {
      return;
    }

    // Calculate price based on selected duration
    const selectedDuration = rebookFormData.duration;
    const durationPrice = DURATIONS_PRICES[selectedDuration as keyof typeof DURATIONS_PRICES] || 50;

    // Prepare botel data for session storage
    const botelData = {
      id: selectedBooking.botel.id,
      name: selectedBooking.botel.name,
      price: durationPrice,
      location: selectedBooking.botel.location_address || selectedBooking.botel.location || '',
      image: selectedBooking.botel.images?.[0] || '/images/botelOne.png',
      checkIn: rebookFormData.dateTime,
      duration: rebookFormData.duration
    };

    // Store in session storage
    sessionStorage.setItem('selectedBotel', JSON.stringify(botelData));

    // Close dialog and navigate
    setRebookDialogOpen(false);
    navigate('/booking-details');
  };

  const handleCloseRebookDialog = () => {
    setRebookDialogOpen(false);
    setRebookFormData({ dateTime: '', duration: '' });
    setRebookErrors({ dateTime: '', duration: '' });
    setSelectedBooking(null);
  };

  return (
    <>
    <style>
      {`
        /* Webkit scrollbar styling for calendar day cells */
        .calendar-day-cell::-webkit-scrollbar {
          width: 3px;
        }
        .calendar-day-cell::-webkit-scrollbar-track {
          background: transparent;
        }
        .calendar-day-cell::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
        }
        .calendar-day-cell:hover::-webkit-scrollbar-track {
          background: #2C353F;
        }
        .calendar-day-cell:hover::-webkit-scrollbar-thumb {
          background: #3D546B;
        }
        .calendar-day-cell:hover::-webkit-scrollbar-thumb:hover {
          background: #4A5F7A;
        }
      `}
    </style>
    <Box sx={{ background: '#151A23', color: '#fff', marginTop: "56px", mb:"-80px", borderTop:"1px solid #2C353F", borderBottom:"1px solid #2C353F", padding: "17px 31px",
      "& svg":{
        fontSize: "24px",
        display: "block",
      }
     }}>
        <Typography sx={{ fontSize: 18, fontWeight: "semibold", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        All Bookings
        </Typography>
        </Box>
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, background: '#151A23', minHeight: '100vh', color: '#fff', marginTop: 10 }}>

  <>
   <div style={styles.container}>
  {loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
      <CircularProgress sx={{ color: '#3776FF' }} />
    </Box>
  ) : (
    <>
      {/* Year + Month Navigation */}
  <div style={styles.headerBar}>
    {/* Month Controls */}
    <button style={styles.navBtn} onClick={goToPrevMonth}>⟨</button>
    <div style={{ fontSize: 18 }}>{monthNames[month]}</div>
    <button style={styles.navBtn} onClick={goToNextMonth}>⟩</button>

    {/* Year Controls */}
    <button style={styles.navBtn} onClick={goToPrevYear}>⟨</button>
    <div style={{ fontSize: 18, fontWeight: "bold" }}>{year}</div>
    <button style={styles.navBtn} onClick={goToNextYear}>⟩</button>

    {/* Legends */}
    <Box sx={{ display: "flex", width:"100%", justifyContent: "flex-end" }}>
      <Typography fontSize={16} fontWeight="bold" sx={{mr:"16px"}}>
        <span style={{color: "#059669", marginRight: "6px"}}>
          <CircleIcon sx={{ fontSize: 14 }} />
        </span>
        Active
      </Typography>

      <Typography fontSize={16} fontWeight="bold" sx={{mr:"16px"}}>
        <span style={{color: "#D1D5DB", marginRight: "6px"}}>
          <CircleIcon sx={{ fontSize: 14 }} />
        </span>
        Past
      </Typography>

      <Typography fontSize={16} fontWeight="bold">
        <span style={{color: "#5B8DFF", marginRight: "6px"}}>
          <CircleIcon sx={{ fontSize: 14 }} />
        </span>
        Future
      </Typography>
    </Box>
  </div>

  {/* Week Header - Fixed */}
  <div style={styles.calendarGrid}>
    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d, index) => (
      <div
        key={d}
        style={{
          ...styles.headerCell,
          borderLeft: index === 0 ? "1px solid #3D546B" : "none",
          borderRight: "1px solid #3D546B",
          borderTop: "1px solid #3D546B",
          borderBottom: "1px solid #3D546B",

          // Rounded corners for first and last header cells
          borderTopLeftRadius: index === 0 ? "12px" : undefined,
          borderTopRightRadius: index === 6 ? "12px" : undefined,
        }}
      >
        {d}
      </div>
    ))}
  </div>

  {/* Calendar Days with borders */}
  <div style={styles.calendarGrid}>
    {cells.map((cell, index) => {
      const colIndex = index % 7; // 0 = first column
      const originalStyle = cell.props.style || {};

      return React.cloneElement(cell, {
        style: {
          ...originalStyle,

          // Always show left border for first column
          borderLeft: colIndex === 0 ? "1px solid #3D546B" : originalStyle.borderLeft || "none",

          borderRight: "1px solid #3D546B",
          borderTop: "1px solid #3D546B",
          borderBottom: "1px solid #3D546B",
        },
      });
    })}
  </div>
    </>
  )}
  </div>
    {/* Current Stay - Render for each active stay */}
      {activeStays.map((activeStay, index) => (
      <Paper key={activeStay.id || index} sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3 }} elevation={4}>
        <Box sx={{ display: 'flex', flexDirection:"row", gap: 2, justifyContent:"space-between" }}>
          <Box>
        <Typography variant="subtitle1" fontWeight="bold" sx={{pb:"10px"}}>
          Current Stay At {activeStay?.botel?.name || 'Botel'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#fffff', mb: 3 }}>
          {activeStay?.check_in ? formatDisplayDate(activeStay.check_in) : 'N/A'}
        </Typography>
        </Box>
        <Box sx={{ display: 'block', gap: 2, mt: 2 }}>
          {/* <Button variant="contained" color="primary" sx={{mr:"20px", fontSize: 16, borderRadius:"10px", backgroundColor:"#2156C4", "&:hover": { opacity: 0.5 } }}> 
            <img
                  src="/icons/button-icon-white.svg"
                  alt="calendar icon"
                  style={{ width: 20, height: 20, marginRight: 10}}
                />Extend Stay</Button> */}
          <Button onClick={() => navigate(`/client/modify-booking/${activeStay?.id || ''}`)} variant="outlined" color="primary" sx={{fontSize: 16, color:"white", borderColor:"white", borderRadius:"10px", "&:hover": { opacity:0.5} }}>
          <img
                  src="/icons/edit-icon.svg"
                  alt="calendar icon"
                  style={{ width: 20, height: 20, marginRight: 10}}
                />
            Modify Booking
            </Button>
        </Box>
        </Box>
        <Grid container spacing={2} alignItems="center" sx={{pb:"5px"}}>
            <Grid size={{xs: 12, md: 4}}>
            <Box sx={{ display: 'flex', flexDirection:"column", alignItems: 'left', gap: 1, backgroundColor:"#131A21", p:"30px", borderRadius:"12px"}}>
              <Typography sx={{fontSize: 16}}>Location</Typography>
              <Box sx={{ display: 'flex', flexDirection:"row", alignItems: 'center', gap: 0.5, ml:"-5px", mt:"5px" }}>
              <LocationOnOutlinedIcon sx={{ color: 'white' }} />
              <Typography sx={{fontSize: 18}}>
                {activeStay?.botel?.location || activeStay?.botel?.location_address || 'N/A'}
              </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{xs: 12, md: 4}}>
          <Box sx={{ display: 'flex', flexDirection:"column", alignItems: 'left', gap: 1, backgroundColor:"#131A21", p:"30px", borderRadius:"12px"}}>
              <Typography sx={{fontSize: 16}}>Total Price</Typography>
              <Box sx={{ display: 'flex', flexDirection:"row", gap: 0.5, ml:"-5px", mt:"5px" }}>
              <img src="/icons/price-tag-icon.svg" alt="alert" />
              <Typography sx={{fontSize: 18, ml:"5px"}}>
                ${Number(activeStay?.total_amount || 0).toFixed(2)}
              </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{xs: 12, md: 4}}>
          <Box sx={{ display: 'flex', flexDirection:"column", alignItems: 'left', gap: 1, backgroundColor:"#131A21", p:"30px", borderRadius:"12px"}}>
              <Typography sx={{fontSize: 16}}>Insurance Status</Typography>
              <Box sx={{ display: 'flex', flexDirection:"row", gap: 0.5, ml:"-5px", mt:"5px" }}>
              <img src="/icons/crown-icon-2.svg" alt="alert" style={{position:"relative", top:"-3px"}}/>
              <Typography sx={{fontSize: 18, ml:"5px", color:"#FFBE45"}}> Premium</Typography>
              </Box>
            </Box>          </Grid>
        </Grid>
      </Paper>
      ))}

      {/* Past Stay */}
      {previousStay && (
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3 }} elevation={4}>
        <Box sx={{ display: 'flex', flexDirection:"row", gap: 2, justifyContent:"space-between" }}>
          <Box>
        <Typography variant="subtitle1" fontWeight="bold" sx={{pb:"10px"}}>
          Past Stay At {previousStay?.botel?.name || 'Botel'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#fffff', mb: 3 }}>
          {previousStay?.check_in ? formatDisplayDate(previousStay.check_in) : 'N/A'}
        </Typography>
        </Box>
        <Box sx={{ display: 'block', gap: 2, mt: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{mr:"20px", fontSize: 16, borderRadius:"10px", backgroundColor:"#2156C4", "&:hover": { opacity: 0.5 } }}
            onClick={() => handleRebookStay(previousStay)}
          > 
          <img
                  src="/icons/edit-icon.svg"
                  alt="calendar icon"
                  style={{ width: 20, height: 20, marginRight: 10}}
                />Rebook This Stay</Button>
         
        </Box>
        </Box>
        <Grid container spacing={2} alignItems="center" sx={{pb:"5px"}}>
            <Grid size={{xs: 12, md: 3}}>
            <Box sx={{ display: 'flex', flexDirection:"column", alignItems: 'left', gap: 1, backgroundColor:"#131A21", p:"30px", borderRadius:"12px"}}>
              <Typography sx={{fontSize: 16}}>Location</Typography>
              <Box sx={{ display: 'flex', flexDirection:"row", alignItems: 'center', gap: 0.5, ml:"-5px", mt:"5px" }}>
              <LocationOnOutlinedIcon sx={{ color: 'white' }} />
              <Typography sx={{fontSize: 18}}>
                {previousStay?.botel?.location || previousStay?.botel?.location_address || 'N/A'}
              </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{xs: 12, md: 3}}>
          <Box sx={{ display: 'flex', flexDirection:"column", alignItems: 'left', gap: 1, backgroundColor:"#131A21", p:"30px", borderRadius:"12px"}}>
              <Typography sx={{fontSize: 16}}>Total Price</Typography>
              <Box sx={{ display: 'flex', flexDirection:"row", gap: 0.5, mt:"5px" }}>
              <img src="/icons/price-tag-icon.svg" alt="alert" />
              <Typography sx={{fontSize: 18, ml:"5px"}}>
                ${Number(previousStay?.total_amount || 0).toFixed(2)}
              </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{xs: 12, md: 3}}>
          <Box sx={{ display: 'flex', flexDirection:"column", alignItems: 'left', gap: 1, backgroundColor:"#131A21", p:"30px", borderRadius:"12px"}}>
              <Typography sx={{fontSize: 16}}>Insurance Status</Typography>
              <Box sx={{ display: 'flex', flexDirection:"row", gap: 0.5, mt:"5px" }}>
              <img src="/icons/insured-icon.svg" alt="alert"/>
              <Typography sx={{fontSize: 18, ml:"5px"}}> Basic</Typography>
              </Box>
            </Box>          </Grid>

            <Grid size={{xs: 12, md: 3}}>
          <Box sx={{ display: 'flex', flexDirection:"column", alignItems: 'left', gap: 1, backgroundColor:"#131A21", p:"30px", borderRadius:"12px"}}>
              <Typography sx={{fontSize: 16}}>Review Status</Typography>
              <Box sx={{ display: 'flex', flexDirection:"row", gap: 0.5, mt:"5px" }}>
              <Button sx={{color:"white", p:"0px"}} startIcon={<StarBorderIcon sx={{fontSize: 26}}/>}>Leave A Review</Button>
              </Box>
            </Box>          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Footer */}
      <Box sx={{ mt: 5, py: 2, borderTop: '1px solid #232B3E', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, color: '#b0b8c1', fontSize: 14 }}>
        <Typography sx={{ fontSize: 14 }}>© 2025 BOTEL. All rights reserved.</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>Terms</Button>
          <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>Privacy</Button>
          <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>Support</Button>
        </Box>
      </Box>

      {/* Rebook Dialog */}
      <Dialog 
        open={rebookDialogOpen} 
        onClose={handleCloseRebookDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1F262C !important',
            backgroundImage: 'none !important',
            borderRadius: 3,
            color: '#fff',
            '&.MuiPaper-root': {
              backgroundColor: '#1F262C !important',
              backgroundImage: 'none !important'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: 24, fontWeight: 'bold' }}>
          Rebook This Stay
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" mb={1} color="#fff" sx={{ fontSize: 16 }}>
                Date & Time
              </Typography>
              <TextField
                fullWidth
                type="datetime-local"
                value={rebookFormData.dateTime}
                onChange={handleRebookInputChange('dateTime')}
                error={!!rebookErrors.dateTime}
                onClick={(e) => {
                  // Open the datetime picker when clicking anywhere in the input
                  const input = e.currentTarget.querySelector('input[type="datetime-local"]') as HTMLInputElement;
                  if (input) {
                    // Try modern showPicker API first
                    if (input.showPicker) {
                      input.showPicker();
                    } else {
                      // Fallback: focus and click the input
                      input.focus();
                      input.click();
                    }
                  }
                }}
                sx={{ 
                  bgcolor: '#1a1f24', 
                  borderRadius: 2, 
                  input: { 
                    color: '#fff',
                    fontSize: 16,
                    cursor: 'pointer'
                  },
                  '& .MuiOutlinedInput-root': {
                    cursor: 'pointer'
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
              {rebookErrors.dateTime && (
                <Typography color="error" variant="caption" sx={{ fontSize: 14, mt: 0.5, display: 'block' }}>
                  {rebookErrors.dateTime}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" mb={1} color="#fff" sx={{ fontSize: 16 }}>
                Duration
              </Typography>
              <Select
                fullWidth
                displayEmpty
                value={rebookFormData.duration}
                onChange={handleRebookSelectChange}
                error={!!rebookErrors.duration}
                sx={{ 
                  bgcolor: '#1a1f24', 
                  color: '#fff', 
                  borderRadius: 2,
                  fontSize: 16
                }}
                renderValue={selected => selected || 'Select Duration'}
              >
                <MenuItem disabled value="">
                  Select Duration
                </MenuItem>
                {DURATIONS.map(d => (
                  <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                ))}
              </Select>
              {rebookErrors.duration && (
                <Typography color="error" variant="caption" sx={{ fontSize: 14, mt: 0.5, display: 'block' }}>
                  {rebookErrors.duration}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleCloseRebookDialog}
            sx={{ 
              color: '#fff', 
              textTransform: 'none',
              borderRadius: "8px",
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRebookSubmit}
            variant="contained"
            sx={{ 
              backgroundColor: '#2156C4',
              color: '#fff',
              textTransform: 'none',
              borderRadius: "8px",
              '&:hover': { backgroundColor: '#1a45a0' }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      </>
    </Box>
    </>
  );
};

export default AllBookingsContent; 